import React, {useRef, createRef, useState, useContext, useEffect} from 'react';
import __ from "lodash";
import moment from "moment";
import DataGrid from "../../../controls/DataGrid";
import {Button, Grid, IconButton} from "@material-ui/core";
import {AddBoxOutlined, DeleteOutline} from "@material-ui/icons";
import {withStyles} from "@material-ui/core/styles";
import TariffLookup from "./TariffLookup";
import {defaultChargeRecord, defaultDgRecord} from "./DefaultChargeRecord";
import {useQueryClient} from "react-query";
import ComboSelect from "../../../controls/ComboSelect";

import SystemContext from "../../../SystemContext";

const styles = {
  header: {
    "&.ag-header-cell": {
      paddingLeft: 3, paddingRight: 3, fontSize: 12
    }
  },
  column: {
    "&.ag-cell-not-inline-editing": {
      paddingLeft:4, paddingRight: 4
    }
  }
};

const getExchangeRate = (from, to , rate) => {
  const roundedRate = isNaN(rate) ? 0 : (Math.round(rate * 10000) / 10000).toFixed(4);
  return (from !== to ? `${from}->${to}: ${roundedRate}` : "");
}

const getDeck = (dimensions) => {
  let deck = "LOWER";
  if (dimensions && Array.isArray(dimensions)) {
    dimensions.forEach(record => {
      if (__.has(record, "deckRequired") && record.deckRequired === "MAIN")
        deck = "MAIN";
    });
    return deck;
  }

  return "MAIN";
}


const BookingRatesGrid = (_props) => {
  const props = {
    ..._props
  }

  const SystemData = useContext(SystemContext);
  const eventBus = SystemData.eventBus;

  const [gridApi, setGridApi] = useState(null);
  //const [gridColumnApi, setGridColumnApi] = useState(null);
  //const [selectedRow, setSelectedRow] = useState(null);
  const [state, setState] = useState({
    canEdit: props.canEdit||false,
    activeRow: null,
    showAddDialog: false,
    addButtonSuffix: "",

  });
  if (props.canEdit && !state.canEdit) {
    setState({...state, canEdit: props.canEdit});
    if (gridApi)
      gridApi.redrawRows();
  }

  const queryClient = useQueryClient();

  const dataGridRef = useRef();
  //const chargeTypeRef = createRef();
  const tariffRef = useRef();
  const record = props.record;
  const spotBooking = record.bookingType === "SPOT";
  const rates = props.rates;
  const deck = getDeck(record.dimensions);
  const baseCurrency = record.gsaDefaultCurrencyCode;
  const {baseToEur, baseToUsd, baseToGbp} = record.gsaCurrencyRates || {baseToUsd: 0, baseToGbp: 0, baseToEur: 0};
  const baseToEurLabel = getExchangeRate(baseCurrency, 'EUR', baseToEur);
  const baseToUsdLabel = getExchangeRate(baseCurrency, 'USD', baseToUsd);
  const baseToGbpLabel = getExchangeRate(baseCurrency, 'GBP', baseToGbp);

  // ==================
  // Grid setup
  // ==================
  const { classes } = props;
  const onGridReady = (params) => {
    setGridApi(params.api);
    //setGridColumnApi(params.columnApi);
    params.api.sizeColumnsToFit();
    if (props.onGridReady) {
      props.onGridReady(params);
    }
  };

  useEffect(()=> {
    const add = async (params) => {
      const {gridApi, dgr} = params;
      const model = gridApi.getModel();
      const visibleRows = model.rowsToDisplay;
      if (dgr === "YES") {
        const dgRecord = { ...defaultDgRecord };
        let noDgrRate = true;
        visibleRows.forEach(node => {
          if (__.has(node?.data, "chargeTypeObject") && node.data.chargeTypeObject?.code === "DG")
            noDgrRate = false;
        });
        if (noDgrRate) {
          if (gridApi && gridApi.getDisplayedRowCount() >= 1) {
            gridApi.setDomLayout('autoHeight');
            // auto height will get the grid to fill the height of the contents,
            // so the grid div should have no height set, the height is dynamic.
            document.querySelector('#chargesGrid').style.height = '';
          }
          gridApi.applyTransaction({add: [dgRecord]})
        }
      } else {
        visibleRows.forEach(node => {
          if (__.has(node?.data, "chargeTypeObject") && node.data.chargeTypeObject?.code === "DG")
            gridApi.applyTransaction({ remove: [node.data]});
        });
      }
    }
    eventBus.bind("Rates.add", add);
    return () => {
      eventBus.unbind("Rates.add", add);
    }
  }, []);

  // const onRowSelected = (params) => {
  //   debugger;
  //   if (params.type === "rowSelected") {
  //     setSelectedRow({
  //       rowIndex: params.rowIndex,
  //       rowData: params.data
  //     })
  //   }
  //   else {
  //     setSelectedRow(null);
  //   }
  // };

  const onRowDataUpdated = (params) => {
    createSummaryData(params);
  }

  function setAutoHeight() {
    if (gridApi && gridApi.getDisplayedRowCount() >= 1) {
      gridApi.setDomLayout('autoHeight');
      // auto height will get the grid to fill the height of the contents,
      // so the grid div should have no height set, the height is dynamic.
      document.querySelector('#chargesGrid').style.height = '';
    }
  }

  // ==================
  // Grid functions
  // ==================
  const getEmptyRow = () => {
    const emptyRow = { ...defaultChargeRecord, chargeType: "Freight Charge", baseRateCurrency: baseCurrency };
    //const columns = getColumns({canEdit: state.canEdit});
    // if (columns && Array.isArray(columns)) {
    //   columns.map(column => {
    //     emptyRow[column.field] = "";
    //   })
    // }
    return (emptyRow);
  };

  const sum = (values, col) => {
    let sum = 0;
    values.forEach( value => {
      sum += (isNaN(value.data[col]) ? 0 : Number(value.data[col]));
    });
    return sum;
  }

  const createSummaryData = (params) => {
    let model;
    let visibleRows = [];
    const activeApi = params.api ? params.api : gridApi;
    if(activeApi){
      model = activeApi.getModel();
      visibleRows = model.rowsToDisplay;
    }
    //console.log('model:', model);
    //console.log('visibleRows:', visibleRows);

    const rateLinesTotal = {};
    const columns = getColumns({canEdit: state.canEdit});

    if (columns && Array.isArray(columns)) {
      columns.map(column => {
        if (column.summary) {
          rateLinesTotal[column.field] = sum(visibleRows, column.field);
        }
      })
    }
    const rateLines = [];
    visibleRows.forEach(row => {
      const rateLine = {...row.data, exchangeRates: {baseToEur, baseToGbp, baseToUsd}}
      rateLines.push(rateLine);
    })

    //console.log('result:', rateLinesTotal);
    if (props.onChange) {
      const totals = {
        eurTotalAmount: baseCurrency === "EUR" ? rateLinesTotal.baseRateTotal : rateLinesTotal.baseRateTotal * baseToEur,
        gbpTotalAmount: baseCurrency === "GBP" ? rateLinesTotal.baseRateTotal : rateLinesTotal.baseRateTotal * baseToGbp,
        usdTotalAmount: rateLinesTotal.usdRateTotal,
        rateLines: rateLines
      }
      props.onChange(totals);
    }
  }

  const chargeTypeValueChange = (params) => {
    const combos = queryClient.getQueryData(['ComboValues', "TARIFF CHARGE CODES", '_cached']);
    if (combos) {
      const selectedCombo = __.find(combos, {code: params.node.data.chargeType});
      if (selectedCombo && !__.isEmpty(selectedCombo)) {
        const {value, data} = selectedCombo;
        const {chargeGroupCode, calculationType} = data;
        const unitType = __.has(data, "unitType") ? data.unitType : "";
        const requestAmount = __.has(data, "requestAmount") ? data.requestAmount : false;
        params.node.data.chargeTypeValue = value;
        params.node.data.chargeGroupCode = chargeGroupCode;
        params.node.data.calculationType = calculationType;
        params.node.data.unitType = unitType;
        params.node.data.requestAmount = requestAmount;
      }
    }

    // change of chargeType may require zeroing some values
    if (params.colDef.field  === "chargeType" && params.oldValue !== params.data.chargeTypeValue) {
      if (["SECURITY", "DEICING"].includes(params.newValue)) {
        params.node.data.quantity = 0;
      }
      if (params.node.data.chargeGroupCode === "ADDITIONAL")
        params.node.data.baseRate = 0;
    }

    createSummaryData(params);
  }

  const handleRemoveItem = (data, gridApi, node) => {
    if (!node)  return;

    const model = gridApi.getModel();
    const visibleRows = model.rowsToDisplay;

    if (visibleRows && visibleRows.length <=1) {

      SystemData.confirm(
        ``,
        `At least one charge record is required`,
        [
          {icon: "", value: true, label: "OK", options: {color:"primary"}},
        ],
        // (response) => {
        //   if (response) {
        //
        //   }
        // }
      );

      return;
    }
    gridApi.applyTransaction({ remove: [node.data]});

  }

  const renderActionButtons = (params) => {
    let disable = false;

    if (params && params.api) {
      const model = params.api.getModel();
      const visibleRows = model.rowsToDisplay;
      disable = visibleRows && visibleRows.length <= 1;
    }

    return (
      <>
        {
          <IconButton
            color="secondary"
            onClick={() => { handleRemoveItem(params.data, params.api, params.node) }}
            disabled={params.disabled}
            size={"small"}
          >
            <DeleteOutline/>
          </IconButton>
        }
      </>
    )
  }

  // ==================
  // Value Getters
  // ==================
  const hashValueGetter = function (params) {
    return params.node.rowIndex + 1;
  };

  const quantityValueGetter = function (params) {
    if (params.data.calculationType && params.data.calculationType === "FIXED")
      return 1;
    if (params.data.unitType) {
      switch(params.data.unitType) {
        case "CHARGEABLE_WEIGHT":
          return record.expectedChargeableWeight;
        case "WEIGHT":
          return record.expectedWeightKg;
        case "PIECES":
          return record.expectedPieces;
        default:
          return params.data.quantity;
      }
    }
  };

  const isQuantityEditable = function (params) {
    // if (params.data.chargeType === "Primary")
    //   return false;
    if (params.data.calculationType && params.data.calculationType === "FIXED")
      return false;
    if (params.data.unitType) {
      switch(params.data.unitType) {
        case "CHARGEABLE_WEIGHT":
        case "WEIGHT":
        case "PIECES":
          return false;
        default:
          return true;
      }
    }
    return true;
  };

  const quantityUnitsValueGetter = function (params) {
    if (params.data.calculationType && params.data.calculationType === "FIXED")
      return "Fixed Charge";
    if (params.data.unitType) {
      switch(params.data.unitType) {
        case "CHARGEABLE_WEIGHT":
          return "KG Chargeable Weight";
        case "WEIGHT":
          return "KG Gross Weight";
        case "PIECES":
          return "Pieces";
        default:
          return __.capitalize(params.data.unitType);
      }
    }
  }

  const baseRateValueGetter = function (params) {
    if ( ! ["CHARGEABLE_WEIGHT", "FIXED"].includes(params.data.unitType) )
      return params.data.baseRate;
    else if (spotBooking && params.data.baseRate) {
      return params.data.baseRate;
    } else if (params.data.tariffRate && params.data.tariffRate.found) {
      return params.data.tariffRate.rate;
    }

    return params.data.baseRate;
  }

  const isBaseRateEditable = function (params) {
    // if (params.data.chargeType === "Primary")
    //   return false;
    if (params.data.calculationType) {
      if (params.data.calculationType === "FIXED")
        return true;
      if (params.data.calculationType === "UNIT" && params.data.unitType && params.data.unitType === "WEIGHT")
        return true;
    }

    if (params.data.unitType) {
      switch(params.data.unitType) {
        case "CHARGEABLE_WEIGHT":
        case "WEIGHT":
          return false;
        case "PIECES":
        default:
          return true;
      }
    }
    return true;
  };

  const totalBaseRateGetter = function (params) {
    if (params.data.quantity && params.data.baseRate) {
      let minimum = 0;
      if (__.has(params.data, "tariffRate") && __.has(params.data.tariffRate, "minimum"))
        minimum = params.data.tariffRate.minimum;
      if (params.data.chargeGroupCode !== "ADDITIONAL")
        return(Math.max(Number(params.data.quantity) * Number(params.data.baseRate), minimum));
      return(Number(params.data.quantity) * Number(params.data.baseRate));
    }
    return "N/A"
  };

  const totalUsdGetter = function (params) {
    if (params.data.quantity && params.data.baseRate) {
      return Number((Math.round(Number(totalBaseRateGetter (params) * baseToUsd) * 100) / 100).toFixed(2));
    }
    return "N/A"
  };

  const baseRateCurrencyGetter = function (params) {
    return(record && record.gsaDefaultCurrencyCode ? record.gsaDefaultCurrencyCode : "Missing");
  }

  // ==================
  // The grid structure
  // ==================
  const numericColumnEditor = {
    component: "MaskedNumber",
    params: {
      isdatagrid: true,
      value: props.value,
      decimalScale: 2,
      fixedDecimalScale: true,
      // onChange: props.onChange,
      style: {width:"100%", fontSize: "0.875rem", textAlign: "right", padding: "12px"},
    }
  }

  const getColumns = (props) => {
    const baseColumn = {
      options: {
        cellStyle: { fontSize: 7, },
        cellClass: classes.column,
        headerClass: classes.header,
      },
      editable: props.canEdit,
      headerClass: 'classes'
    };

    return [
      {
        headerName: "#",
        ...baseColumn,
        field: "sequence",
        maxWidth: 50,
        summary: false,
        editable: false,
        navigable: false,
        valueGetter: (params) => {
          const sequence = hashValueGetter(params);
          params.node.data.sequence = sequence;
          return sequence;
        },
      },
      {
        headerName: "Charge Type",
        ...baseColumn,
        field: "chargeType",
        width: 200,
        summary: false,
        editable: true,
        cellEditor: {
          component: "ComboSelect",
          params: {
            id: "chargeType",
            isdatagrid: true,
            value: props.value,
            group: "TARIFF CHARGE CODES",
            subGroup: "",
            style: {width:"100%", fontSize: "0.875rem", textAlign: "left", padding: "12px"},
          }
        },
        valueFormatter: (params) => {
          const combos = queryClient.getQueryData(['ComboValues', "TARIFF CHARGE CODES", '_cached']);
          if (combos) {
            const selectedCombo = __.find(combos, {code: params.node.data.chargeType});
            if (selectedCombo && !__.isEmpty(selectedCombo)) {
              const {value} = selectedCombo;
              return value;
            }
          }
          return params.value;
        },
        onCellValueChanged: chargeTypeValueChange,
      },
      // {
      //   headerName: "",
      //   ...baseColumn,
      //   field: "chargeObject",
      //   width: 0,
      //   summary: false,
      //   editable: false,
      //   navigable: false,
      //   valueGetter: (params) => {
      //     const combos = queryClient.getQueryData(['ComboValues', "TARIFF CHARGE CODES", "", '_cached']);
      //     if (combos) {
      //       const selectedCombo = __.find(combos, {code: params.node.data.chargeType});
      //       if (selectedCombo && !__.isEmpty(selectedCombo)) {
      //         const {value, data} = selectedCombo;
      //         const {chargeGroupCode, calculationType} = data;
      //         const unitType = __.has(data, "unitType") ? data.unitType : "";
      //         const requestAmount = __.has(data, "requestAmount") ? data.requestAmount : false;
      //         params.node.data.chargeTypeValue = value;
      //         params.node.data.chargeGroupCode = chargeGroupCode;
      //         params.node.data.calculationType = calculationType;
      //         params.node.data.unitType = unitType;
      //         params.node.data.requestAmount = requestAmount;
      //       }
      //       const chargeObject = params.node.data.chargeType;
      //       params.node.data.chargeObject = chargeObject;
      //       return chargeObject;
      //     }
      //   },
      //   cellRenderer: params => ``,
      //   onCellValueChanged: createSummaryData,
      // },
      {
        headerName: "Quantity",
        ...baseColumn,
        field: "quantity",
        width: 110,
        type: "rightAligned",
        options: { cellClass: `ag-right-aligned-cell ${classes.column}`, headerClass: `ag-right-aligned-header ${classes.header}` },
        summary: false,
        cellEditor: numericColumnEditor,
        editable: params => isQuantityEditable(params),
        valueGetter: (params) => {
          const quantity = quantityValueGetter(params);
          params.node.data.quantity = quantity;
          return quantity;
        },
        valueFormatter: (params)=> {
          return params.value ? Number(params.value).toLocaleString() : "";
        },
        onCellValueChanged: createSummaryData,
      },
      {
        headerName: " ",
        ...baseColumn,
        field: "quantityUnits",
        width: 200,
        editable: false,
        summary: false,
        valueGetter: quantityUnitsValueGetter,
      },
      {
        headerName: " ",
        ...baseColumn,
        field: "tariffRate",
        width: 5,
        type: "numericColumn",
        options: { cellClass: `ag-right-aligned-cell ${classes.column}`, headerClass: `ag-right-aligned-header ${classes.header}` },
        summary: false,
        editable: false,
        navigable: false,
        cellRendererSelector: (params) => {return {
          component: "Wrapper",
          params: {
            component: (
              <TariffLookup
                ref={tariffRef}
                origin={record.charterOrigin}
                destination={record.destination}
                companyUuid={record.gsaCompanyUuid}
                chargeCode={"FREIGHT"}
                chargeDate={moment().format("YYYY-MM-DD")}
                promotionCode={""}
                quantity={params.node.data.quantity}
                shcs={record.shcs || []}
                // shcs={["ALL"]}
                deck={deck || "MAIN"}
                updateParams={params}
                updateRate={(params, tariffData) => {
                  if (tariffRef.current) {
                    params.node.data.tariffRate = tariffData;
                    params.api.refreshCells();
                    createSummaryData(params);
                  }
                }}
              />
            )
          }
        }},
        onCellValueChanged: createSummaryData,
      },
      {
        headerName: `${spotBooking ? "Rate" : "Base Rate"}`,
        ...baseColumn,
        field: "baseRate",
        width: 150,
        type: "numericColumn",
        options: { cellClass: `ag-right-aligned-cell ${classes.column}`, headerClass: `ag-right-aligned-header ${classes.header}` },
        summary: false,
        editable: params => isBaseRateEditable(params),
        // cellEditor: numericColumnEditor,
        cellEditor: {
          component: "MaskedNumber",
          params: {
            isdatagrid: true,
            value: props.value,
            decimalScale: 2,
            fixedDecimalScale: true,
            // onChange: props.onChange,
            style: {width:"100%", fontSize: "0.875rem", textAlign: "right", padding: "12px"},
          }
        },
        valueGetter: (params) => {
          const baseRate = baseRateValueGetter(params);
          params.node.data.baseRate = baseRate;
          return baseRate;
        },
        onCellValueChanged: createSummaryData,
        valueFormatter: (params)=> {
          return params.value ? Number(params.value).toLocaleString() : "";
        }
      },
      {
        headerName: " ",
        ...baseColumn,
        field: "baseRateCurrency",
        width: 110,
        editable: false,
        navigable: false,
        summary: false,
        valueGetter: (params) => {
          const baseRateCurrency = baseRateCurrencyGetter(params);
          params.node.data.baseRateCurrency = baseRateCurrency;
          return baseRateCurrency;
        },
      },
      {
        headerName: `Total (${record.gsaDefaultCurrencyCode})`,
        ...baseColumn,
        field: "baseRateTotal",
        width: 160,
        type: "numericColumn",
        options: { cellClass: `ag-right-aligned-cell ${classes.column}`, headerClass: `ag-right-aligned-header ${classes.header}` },
        summary: true,
        editable: false,
        navigable: false,
        valueGetter: (params) => {
          const baseRateTotal = totalBaseRateGetter(params);
          params.node.data.baseRateTotal = baseRateTotal;
          return baseRateTotal;
        },
        valueFormatter: (params)=> {
          let minimum = 0;
          if (__.has(params.data, "tariffRate") && __.has(params.data.tariffRate, "minimum"))
            minimum = params.data.tariffRate.minimum;
          if (minimum > 0 && params.value && !isNaN(params.value) && params.value === minimum)
            return `Min charge =  ${Number(params.value).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}`
          return params.value && !isNaN(params.value) ? Number(params.value).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2}) : "";
        }
      },
      {
        headerName: "Total $",
        ...baseColumn,
        field: "usdRateTotal",
        type: "numericColumn",
        options: { cellClass: `ag-right-aligned-cell ${classes.column}`, headerClass: `ag-right-aligned-header ${classes.header}` },
        summary: true,
        editable: false,
        navigable: false,
        valueGetter: (params) => {
          const usdRateTotal = totalUsdGetter(params);
          params.node.data.usdRateTotal = usdRateTotal;
          return usdRateTotal;
        },
        valueFormatter: (params)=> {
          return params.value && !isNaN(params.value) ? Number(params.value).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2}) : "";
        }
      },
      {
        headerName: ' ',
        ...baseColumn,
        field: 'deleteAction',
        editable: false,
        summary: false,
        cellRenderer: {
          component: "renderActionButtons",
          // params: {
          //   disabled: !props.canEdit
          // }
        },
        maxWidth: 50,
        cellStyle: { border: "none" },
      },
    ];
  };

  return (
    // <div style={{width: props.width?props.width:"100%", ...props.style}} className="ag-theme-material">
    <div style={{width: props.width?props.width:"100%", ...props.style}} >

      <Grid container spacing={3}>
        <Grid item xs={3}>
          <Button
            style={{height:24, marginTop:4}}
            variant="text"
            color="primary"
            startIcon={<AddBoxOutlined/>}
            disabled={!props.canEdit}
            //className={classes.button}
            onClick={()=>{
              setAutoHeight();
              gridApi.applyTransaction({add: [getEmptyRow()]})
            }}
          >
            Add Charge
          </Button>
        </Grid>
        <Grid item xs={3}>
          <Button variant="text" color="primary">{baseToEurLabel}</Button>
        </Grid>
        <Grid item xs={3}>
          <Button variant="text" color="primary">{baseToGbpLabel}</Button>
        </Grid>
        <Grid item xs={3}>
          <Button variant="text" color="primary">{baseToUsdLabel}</Button>
        </Grid>
      </Grid>

      <DataGrid
        id="chargesGrid"
        title="Rates"
        ref={dataGridRef}
        components={{renderActionButtons}}
        columns={getColumns({canEdit: state.canEdit, classes})}
        rows={rates}
        height={props.height||`calc(100vh - ${(290)}px)`}
        onGridReady={onGridReady}
        //onRowSelected={onRowSelected}
        onRowDataUpdated={onRowDataUpdated}
        //groupIncludeFooter={true}
        //groupIncludeTotalFooter={true}

        options={{
          singleClickEdit: true,
          suppressCellSelection: true,
          //editType:'fullRow',
          //onCellValueChanged: (params)=>{createSummaryData(params)}, //prevent default onCellonCellValueChanged
          defaultColDef: {
            editable: true,
            resizable: false,
            menuTabs: []
          },
          overlayLoadingTemplate:
            '<span class="ag-overlay-loading-center">Please wait while your data are loading</span>',
          overlayNoRowsTemplate:
            '<span style="padding: 10px; border: 1px solid #111; background: #fdecea;">\'Add Charge\' to specify booking charges</span>',
        }}
      />
    </div>
  );

}

export default withStyles(styles)(BookingRatesGrid);
